import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { AssistantService } from '@/cloud/services/assistant.service';
import { TenantService } from '@/cloud/services/tenant.service';
import { AssistantBlock, AssistantDisplayItem, AssistantMessage, AssistantScope, ReplySegment, ReplyStatus, replyStatus, segmentsOf } from '@/cloud/models/assistant.models';
import { TranslationService } from '@/layout/service/translation.service';
import { fill } from '@/layout/service/assistant.translations';
import { AssistantComposerComponent } from './composer/assistant-composer.component';
import { AssistantMarkdownPipe } from './assistant-markdown.pipe';
import { blockComponent } from './blocks/block-registry';
import { timeAgo, toolIcon, toolLabel } from './assistant-format';
import { conversationTranscript } from './assistant-transcript';

/**
 * The assistant conversation: before anything is asked it shows the pulse and starter prompts; answers stream in
 * as text, a live step list, rich blocks and follow-up chips. Hosted by the side drawer and by the full-page
 * service desk, which share one conversation through AssistantService.
 */
@Component({
    selector: 'app-assistant-chat',
    standalone: true,
    imports: [TooltipModule, NgComponentOutlet, NgTemplateOutlet, AssistantComposerComponent, AssistantMarkdownPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { '[class.page]': 'isPage()' },
    template: `
        <div class="ad" [class.service-desk]="assistant.scope() === 'serviceDesk'" [class.ad--page]="isPage()">
            @if (isPage()) {
                <aside class="ad-rail" [attr.aria-label]="t()('assistant.history')">
                    <button type="button" class="rail-new" (click)="newChat()"><i class="pi pi-plus" aria-hidden="true"></i>{{ t()('assistant.newChat') }}</button>
                    <h3 class="section-label">{{ t()('assistant.recent') }}</h3>
                    <ng-container *ngTemplateOutlet="historyList" />
                </aside>
            }
            <div class="ad-main">
                <header class="ad-header">
                    <span class="ad-mark" aria-hidden="true"><i class="pi pi-sparkles"></i></span>
                    <div class="ad-title">
                        <span class="ad-name">{{ assistant.activeTitle() || t()('assistant.title') }}</span>
                        <span class="ad-sub">{{ subtitle() }}</span>
                    </div>
                    <button
                        type="button"
                        class="icon-btn history-toggle"
                        [class.on]="showHistory()"
                        (click)="showHistory.set(!showHistory())"
                        [pTooltip]="t()('assistant.history')"
                        tooltipPosition="bottom"
                        [attr.aria-label]="t()('assistant.history')"
                        [attr.aria-expanded]="showHistory()"
                    >
                        <i class="pi pi-history"></i>
                    </button>
                    <button type="button" class="icon-btn" (click)="newChat()" [pTooltip]="t()('assistant.newChat')" tooltipPosition="bottom" [attr.aria-label]="t()('assistant.newChat')">
                        <i class="pi pi-plus"></i>
                    </button>
                    @if (!isPage() && assistant.wideScreen()) {
                        <button
                            type="button"
                            class="icon-btn dock-toggle"
                            [class.on]="assistant.dockPreference() === 'docked'"
                            (click)="assistant.setDock(assistant.dockPreference() === 'docked' ? 'overlay' : 'docked')"
                            [pTooltip]="t()(assistant.dockPreference() === 'docked' ? 'assistant.undock' : 'assistant.dock')"
                            tooltipPosition="bottom"
                            [attr.aria-label]="t()(assistant.dockPreference() === 'docked' ? 'assistant.undock' : 'assistant.dock')"
                            [attr.aria-pressed]="assistant.dockPreference() === 'docked'"
                        >
                            <i class="pi pi-thumbtack"></i>
                        </button>
                    }
                    @if (!isPage()) {
                        <button type="button" class="icon-btn" (click)="closed.emit()" [attr.aria-label]="t()('assistant.close')"><i class="pi pi-times"></i></button>
                    }
                </header>

                @if (!isPage() && assistant.tenantEnabled() && assistant.serviceDeskAvailable()) {
                    <div class="ad-scope" role="radiogroup" aria-label="Scope">
                        <button type="button" role="radio" [attr.aria-checked]="assistant.scope() === 'tenant'" [class.active]="assistant.scope() === 'tenant'" (click)="setScope('tenant')">
                            <i class="pi pi-building" aria-hidden="true"></i>{{ t()('assistant.scopeTenant') }}
                        </button>
                        <button type="button" role="radio" [attr.aria-checked]="assistant.scope() === 'serviceDesk'" [class.active]="assistant.scope() === 'serviceDesk'" (click)="setScope('serviceDesk')">
                            <i class="pi pi-globe" aria-hidden="true"></i>{{ t()('assistant.scopeAll') }}
                        </button>
                    </div>
                }
                @if (assistant.scope() === 'serviceDesk') {
                    <div class="ad-band"><i class="pi pi-shield" aria-hidden="true"></i>{{ serviceDeskBand() }}</div>
                }
                @if (assistant.pageChip(); as chip) {
                    <div class="ad-context">
                        <span class="context-chip">
                            <i [class]="chip.kind === 'device' ? 'pi pi-server' : 'pi pi-map-marker'" aria-hidden="true"></i>{{ chip.label }}
                            <button type="button" (click)="assistant.pageChipDismissed.set(true)" [attr.aria-label]="t()('assistant.contextRemove')" [pTooltip]="t()('assistant.contextRemove')">
                                <i class="pi pi-times" aria-hidden="true"></i>
                            </button>
                        </span>
                    </div>
                }

                <div class="ad-body" #scroller (scroll)="onScroll()">
                    @if (showHistory()) {
                        <ng-container *ngTemplateOutlet="historyList" />
                    } @else if (assistant.messages().length === 0) {
                        <section class="ad-pulse">
                            <div class="hello">
                                <span class="orb" aria-hidden="true"><i class="pi pi-sparkles"></i></span>
                                @if (assistant.pulse(); as pulse) {
                                    <h2>{{ pulse.headline }}</h2>
                                } @else {
                                    <h2 class="muted">{{ assistant.pulseLoading() ? t()('assistant.pulseLoading') : t()('assistant.title') }}</h2>
                                }
                            </div>
                            @if (assistant.pulse(); as pulse) {
                                <!-- The full page shows these figures in its overview panel instead. -->
                                @if (!isPage()) {
                                    <div class="stats">
                                        @for (s of pulse.stats; track s.key) {
                                            <button type="button" class="stat" [attr.data-tone]="s.value > 0 ? s.tone : 'success'" (click)="ask(s.prompt)" [pTooltip]="s.prompt" tooltipPosition="bottom">
                                                <span class="stat-value">{{ s.value }}</span>
                                                <span class="stat-label">{{ s.label }}</span>
                                            </button>
                                        }
                                    </div>
                                    @if (pulse.tenants.length) {
                                        <h3 class="section-label">{{ t()('assistant.tenantsTitle') }}</h3>
                                        <div class="tenant-chips">
                                            @for (tn of pulse.tenants.slice(0, 6); track tn.namespaceId) {
                                                <button type="button" class="tenant-chip" [class.hot]="tn.offline > 0" (click)="ask(askAbout(tn.name))">
                                                    <span>{{ tn.name }}</span>
                                                    <span class="muted">{{ tn.offline }}/{{ tn.devices }} offline</span>
                                                </button>
                                            }
                                        </div>
                                    }
                                }
                                <h3 class="section-label">{{ t()('assistant.startersTitle') }}</h3>
                                <div class="starters">
                                    @for (p of pulse.prompts; track p) {
                                        <button type="button" class="starter" (click)="ask(p)"><i class="pi pi-arrow-right" aria-hidden="true"></i>{{ p }}</button>
                                    }
                                </div>
                            } @else if (assistant.pulseLoading()) {
                                <div class="stats">
                                    @for (i of [1, 2, 3]; track i) {
                                        <span class="stat skeleton"></span>
                                    }
                                </div>
                            }
                        </section>
                    } @else {
                        <ol class="ad-messages" aria-live="polite">
                            @for (m of assistant.messages(); track m.id; let last = $last) {
                                @if (m.role === 'user') {
                                    <li class="msg user">
                                        <div class="bubble">{{ userText(m) }}</div>
                                    </li>
                                } @else {
                                    <li class="msg assistant">
                                        <span class="avatar" aria-hidden="true"><i class="pi pi-sparkles"></i></span>
                                        <div class="reply">
                                            @for (seg of segments(m); track $index) {
                                                @switch (seg.kind) {
                                                    @case ('text') {
                                                        <div class="md" [innerHTML]="textOf(seg) | assistantMarkdown"></div>
                                                    }
                                                    @case ('steps') {
                                                        <details class="steps" [open]="stepsOf(seg).running">
                                                            <summary>
                                                                @if (stepsOf(seg).running) {
                                                                    <i class="pi pi-spin pi-spinner" aria-hidden="true"></i><span>{{ t()('assistant.stepsRunning') }}</span>
                                                                } @else {
                                                                    <i class="pi pi-check-circle done" aria-hidden="true"></i><span>{{ stepsLabel(stepsOf(seg).steps.length) }}</span>
                                                                }
                                                                <span class="tool-icons">
                                                                    @for (s of toolSteps(stepsOf(seg).steps); track $index) {
                                                                        <i [class]="icon(s.toolName)" aria-hidden="true"></i>
                                                                    }
                                                                </span>
                                                            </summary>
                                                            <ul>
                                                                @for (s of stepsOf(seg).steps; track $index) {
                                                                    @if (s.kind === 'progress') {
                                                                        <li class="step-note">{{ s.text }}</li>
                                                                    } @else {
                                                                        <li class="step-tool" [class.failed]="s.toolFailed">
                                                                            @if (s.toolStatus === 'running') {
                                                                                <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
                                                                            } @else if (s.toolFailed) {
                                                                                <i class="pi pi-times-circle" aria-hidden="true"></i>
                                                                            } @else {
                                                                                <i [class]="icon(s.toolName)" aria-hidden="true"></i>
                                                                            }
                                                                            <span>{{ label(s.toolName) }}</span>
                                                                            @if (s.toolFailed) {
                                                                                <span class="muted">· {{ t()('assistant.toolFailed') }}</span>
                                                                            }
                                                                        </li>
                                                                    }
                                                                }
                                                            </ul>
                                                        </details>
                                                    }
                                                    @case ('block') {
                                                        @if (component(blockOf(seg)); as comp) {
                                                            <div class="block">
                                                                <ng-container *ngComponentOutlet="comp; inputs: { block: blockOf(seg) }" />
                                                            </div>
                                                        }
                                                    }
                                                    @case ('suggestions') {
                                                        @if (last && !assistant.streaming()) {
                                                            <div class="suggestions">
                                                                @for (s of suggestionsOf(seg); track s) {
                                                                    <button type="button" class="suggestion" (click)="ask(s)">{{ s }}</button>
                                                                }
                                                            </div>
                                                        }
                                                    }
                                                    @case ('error') {
                                                        <div class="err" [attr.data-code]="errorOf(seg).code" role="alert">
                                                            <i class="pi pi-exclamation-circle" aria-hidden="true"></i>{{ errorOf(seg).text }}
                                                            @if (errorOf(seg).code === 'too_long' && last) {
                                                                <button type="button" class="err-action" (click)="newChat()">{{ t()('assistant.newChat') }}</button>
                                                            }
                                                        </div>
                                                    }
                                                }
                                            }
                                            @if (m.streaming) {
                                                @let st = status(m);
                                                <div class="status-line" role="status" aria-live="polite">
                                                    <span class="spark" aria-hidden="true">✻</span>
                                                    <span class="verb">{{ st.verb }}…</span>
                                                    @if (st.detail) {
                                                        <span class="detail">{{ st.detail }}</span>
                                                    }
                                                    <span class="elapsed">{{ elapsed() }}</span>
                                                    <button type="button" class="stop-link" (click)="assistant.stop()">{{ t()('assistant.stop') }}</button>
                                                </div>
                                            }
                                        </div>
                                    </li>
                                }
                            }
                        </ol>
                    }
                </div>

                <footer class="ad-footer">
                    <app-assistant-composer [starters]="assistant.pulse()?.prompts ?? []" />
                    @if (assistant.messages().length && !showHistory()) {
                        <div class="copy-conversation">
                            <button
                                type="button"
                                class="icon-btn"
                                (click)="copyConversation()"
                                [disabled]="assistant.streaming() || copying()"
                                [pTooltip]="t()('assistant.copyConversation')"
                                tooltipPosition="top"
                                [attr.aria-label]="t()('assistant.copyConversation')"
                            >
                                <i class="pi pi-copy" aria-hidden="true"></i>
                            </button>
                            <span role="status">{{ copyStatus() ? t()(copyStatus()) : '' }}</span>
                        </div>
                    }
                    @if (!assistant.canAct()) {
                        <p class="read-only"><i class="pi pi-eye" aria-hidden="true"></i>{{ t()('assistant.readOnly') }}</p>
                    }
                </footer>
            </div>
        </div>

        <ng-template #historyList>
            <nav class="ad-history" [attr.aria-label]="t()('assistant.history')">
                @for (c of assistant.conversations(); track c.id) {
                    <div class="hist-row" [class.active]="c.id === assistant.activeId()">
                        <button type="button" class="hist-open" (click)="open(c.id)">
                            <span class="hist-title">{{ c.title }}</span>
                            <span class="hist-time">{{ ago(c.updatedUtc) }}</span>
                        </button>
                        <button type="button" class="icon-btn small" (click)="remove(c.id)" [attr.aria-label]="t()('assistant.deleteConversation')"><i class="pi pi-trash"></i></button>
                    </div>
                } @empty {
                    <p class="muted center">{{ t()('assistant.noHistory') }}</p>
                }
            </nav>
        </ng-template>
    `,
    styleUrl: './assistant-chat.component.scss'
})
export class AssistantChatComponent {
    /** `drawer` is the side panel; `page` is the full-window service desk with a permanent history rail. */
    readonly layout = input<'drawer' | 'page'>('drawer');
    readonly closed = output<void>();
    readonly isPage = computed(() => this.layout() === 'page');
    readonly assistant = inject(AssistantService);
    private tenants = inject(TenantService);
    readonly t = inject(TranslationService).t;

    private scroller = viewChild<ElementRef<HTMLElement>>('scroller');
    private composer = viewChild(AssistantComposerComponent);
    readonly showHistory = signal(false);
    readonly copying = signal(false);
    readonly copyStatus = signal('');
    private stickToBottom = true;

    readonly subtitle = computed(() => (this.assistant.scope() === 'serviceDesk' ? this.t()('assistant.serviceDesk') : (this.tenants.selectedTenant()?.name ?? '')));

    /** Put the cursor in the composer (⌘K on the full page). */
    focus(): void {
        this.composer()?.focus();
    }

    readonly serviceDeskBand = computed(() => fill(this.t()('assistant.serviceDeskBand'), { count: this.assistant.status()?.serviceDesk?.tenantCount ?? 0 }));

    constructor() {
        // Keep the newest output in view while it streams, unless the user scrolled up to read.
        effect(() => {
            this.assistant.messages();
            this.copyStatus.set('');
            if (!this.stickToBottom) return;
            requestAnimationFrame(() => {
                const el = this.scroller()?.nativeElement;
                if (el) el.scrollTop = el.scrollHeight;
            });
        });
        effect(() => {
            if (this.assistant.open() || this.isPage()) setTimeout(() => this.composer()?.focus(), 150);
        });
        effect((onCleanup) => {
            if (this.assistant.streamStartedAt() === null) return;
            this.now.set(Date.now());
            const tick = setInterval(() => this.now.set(Date.now()), 500);
            onCleanup(() => clearInterval(tick));
        });
    }

    segments(m: AssistantMessage): ReplySegment[] {
        return segmentsOf(m.items, !!m.streaming);
    }

    async copyConversation(): Promise<void> {
        const messages = this.assistant.messages();
        if (!messages.length || this.assistant.streaming() || this.copying()) return;
        this.copying.set(true);
        this.copyStatus.set('');
        try {
            const transcript = conversationTranscript(
                messages,
                this.assistant.activeTitle() || this.t()('assistant.title'),
                {
                    user: this.t()('assistant.you'),
                    assistant: this.t()('assistant.title')
                },
                this.assistant.actions()
            );
            if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
                try {
                    await navigator.clipboard.write([
                        new ClipboardItem({
                            'text/html': new Blob([transcript.html], { type: 'text/html' }),
                            'text/plain': new Blob([transcript.text], { type: 'text/plain' })
                        })
                    ]);
                } catch {
                    await navigator.clipboard.writeText(transcript.text);
                }
            } else {
                await navigator.clipboard.writeText(transcript.text);
            }
            if (this.assistant.messages() === messages) this.copyStatus.set('assistant.conversationCopied');
        } catch {
            if (this.assistant.messages() === messages) this.copyStatus.set('assistant.copyFailed');
        } finally {
            this.copying.set(false);
        }
    }

    userText(m: AssistantMessage): string {
        return m.items.find((i) => i.kind === 'text')?.text ?? '';
    }

    component = blockComponent;

    textOf(seg: ReplySegment): string {
        return seg.kind === 'text' ? seg.text : '';
    }

    stepsOf(seg: ReplySegment): { steps: AssistantDisplayItem[]; running: boolean } {
        return seg.kind === 'steps' ? seg : { steps: [], running: false };
    }

    blockOf(seg: ReplySegment): AssistantBlock | null {
        return seg.kind === 'block' ? seg.block : null;
    }

    suggestionsOf(seg: ReplySegment): string[] {
        return seg.kind === 'suggestions' ? seg.suggestions : [];
    }

    errorOf(seg: ReplySegment): { text: string; code?: string } {
        return seg.kind === 'error' ? seg : { text: '' };
    }
    icon = toolIcon;
    label = toolLabel;

    /** Ticks while an answer streams, so the status line's elapsed time moves. */
    private readonly now = signal(Date.now());
    readonly elapsed = computed(() => {
        const started = this.assistant.streamStartedAt();
        if (started === null) return '';
        const seconds = Math.max(0, Math.floor((this.now() - started) / 1000));
        return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    });

    status(m: AssistantMessage): ReplyStatus {
        return replyStatus(m.items);
    }

    toolSteps(steps: { kind: string; toolName?: string | null }[]): { toolName?: string | null }[] {
        return steps.filter((s) => s.kind === 'tool').slice(0, 6);
    }

    stepsLabel(count: number): string {
        return fill(this.t()('assistant.steps'), { count });
    }

    askAbout(name: string): string {
        return `What's going on at ${name}?`;
    }

    ago(value: string): string {
        return timeAgo(value) ?? '';
    }

    ask(prompt: string): void {
        this.showHistory.set(false);
        this.stickToBottom = true;
        this.assistant.send(prompt);
    }

    newChat(): void {
        this.showHistory.set(false);
        this.assistant.startNew();
        this.assistant.loadPulse();
        this.composer()?.focus();
    }

    async open(id: string): Promise<void> {
        this.showHistory.set(false);
        this.stickToBottom = true;
        await this.assistant.openConversation(id);
    }

    async remove(id: string): Promise<void> {
        await this.assistant.deleteConversation(id).catch(() => undefined);
    }

    setScope(scope: AssistantScope): void {
        this.showHistory.set(false);
        this.assistant.setScope(scope);
    }

    onScroll(): void {
        const el = this.scroller()?.nativeElement;
        if (!el) return;
        this.stickToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    }
}
